import React, { useState, useRef, useEffect } from "react";
import "./PhotoCapture.css";

function PhotoCapture({ onBack, onContinue, isSubmitting = false }) {
  const [captureMode, setCaptureMode] = useState("guide"); // guide, camera, review
  const [capturedImage, setCapturedImage] = useState(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [statusMessage, setStatusMessage] = useState(
    "Position your face inside the frame"
  );

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const detectionIntervalRef = useRef(null);

  useEffect(() => {
    if (captureMode === "camera") {
      startCamera();
    }
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureMode]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 1280, height: 720 },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadeddata = () => {
          startFaceDetection();
        };
      }
    } catch (error) {
      console.error("Camera access error:", error);
      alert("Unable to access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
  };

  const detectFaceInSelfieFrame = (imageData, width, height) => {
    const data = imageData.data;
    const centerX = width / 2;
    const centerY = height / 2;

    // Custom oval dimensions matching the on-screen frame (1280x720 coordinates)
    const rx = 150; // Horizontal radius in camera pixels
    const ry = 210; // Vertical radius in camera pixels

    let skinPixelCount = 0;
    let totalPixels = 0;

    // Scan oval region
    for (let y = Math.max(0, centerY - ry); y < Math.min(height, centerY + ry); y++) {
      for (let x = Math.max(0, centerX - rx); x < Math.min(width, centerX + rx); x++) {
        const dx = x - centerX;
        const dy = y - centerY;

        // Ellipse equation: (x/rx)^2 + (y/ry)^2 <= 1
        if ((dx * dx) / (rx * rx) + (dy * dy) / (ry * ry) <= 1) {
          const idx = (y * width + x) * 4;
          const r = data[idx];
          const g = data[idx + 1];
          const b = data[idx + 2];

          // skin tone detection
          const isSkinTone =
            r > 95 &&
            g > 40 &&
            b > 20 &&
            r > g &&
            r > b &&
            Math.abs(r - g) > 15;

          if (isSkinTone) {
            skinPixelCount++;
          }
          totalPixels++;
        }
      }
    }

    const faceRatio = skinPixelCount / totalPixels;
    return {
      faceRatio,
    };
  };

  const startFaceDetection = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    detectionIntervalRef.current = setInterval(() => {
      if (!video || video.readyState !== 4) return;

      const ctx = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const detection = detectFaceInSelfieFrame(
        imageData,
        canvas.width,
        canvas.height
      );

      const ratio = detection.faceRatio;
      let message = "Position your face inside the frame";
      let isFaceOk = false;

      if (ratio >= 0.10 && ratio <= 0.38) {
        message = "✓ Perfect distance - Ready to capture";
        isFaceOk = true;
      } else if (ratio > 0.38) {
        message = "Too close! Please move further away";
      } else if (ratio > 0.02 && ratio < 0.10) {
        message = "Too far! Please move a bit closer";
      }

      setFaceDetected(isFaceOk);
      setStatusMessage(message);
    }, 100);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      // For selfie, we usually want a square-ish or portrait crop
      // Let's just take the full frame and deal with it via CSS for now
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");

      // Draw image from video directly without flipping
      ctx.drawImage(video, 0, 0);

      canvas.toBlob(
        (blob) => {
          setCapturedImage(blob);
          setCaptureMode("review");
          stopCamera();
        },
        "image/jpeg",
        0.95,
      );
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCapturedImage(file);
      setCaptureMode("review");
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setCaptureMode("camera");
  };

  const handleConfirm = () => {
    onContinue(capturedImage);
  };

  // Guide Screen
  if (captureMode === "guide") {
    return (
      <div className="photo-capture-container">
        <div className="photo-capture-card">
          <div className="photo-capture-header">
            <button className="back-button" onClick={onBack}>
              ←
            </button>
            <div className="header-progress">
              <div
                className="header-progress-fill"
                style={{ width: "85%" }}
              ></div>
            </div>
          </div>

          <h2 className="photo-capture-title">Take a selfie</h2>
          <p className="photo-capture-subtitle">
            We need a clear photo of your face to verify your identity
          </p>

          <div className="guide-section">
            <div className="photo-frame">
              <svg viewBox="0 0 100 100" className="frame-illustration">
                <circle
                  cx="50"
                  cy="40"
                  r="25"
                  fill="none"
                  stroke="#121212"
                  strokeWidth="2"
                  strokeDasharray="3,3"
                />
                <path
                  d="M20 90 Q50 60 80 90"
                  fill="none"
                  stroke="#121212"
                  strokeWidth="2"
                  strokeDasharray="3,3"
                />
              </svg>
            </div>

            <div className="guidelines-list">
              <div className="guideline-item">
                <svg
                  className="guideline-icon check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <polyline
                    points="20 6 9 17 4 12"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Look straight at the camera</span>
              </div>
              <div className="guideline-item">
                <svg
                  className="guideline-icon check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <polyline
                    points="20 6 9 17 4 12"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Remove glasses or hat if possible</span>
              </div>
              <div className="guideline-item">
                <svg
                  className="guideline-icon check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <polyline
                    points="20 6 9 17 4 12"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Ensure good lighting on your face</span>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="action-btn capture-btn"
              onClick={() => setCaptureMode("camera")}
            >
              <svg
                className="btn-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
                  strokeWidth="2"
                />
                <circle cx="12" cy="13" r="4" strokeWidth="2" />
              </svg>
              Take Selfie
            </button>
            <button
              className="action-btn upload-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              <svg
                className="btn-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <polyline
                  points="17 8 12 3 7 8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <line
                  x1="12"
                  y1="3"
                  x2="12"
                  y2="15"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Upload Photo
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />

          <div className="photo-capture-footer">Secured by VerifiLite</div>
        </div>
      </div>
    );
  }

  // Camera Screen
  if (captureMode === "camera") {
    return (
      <div className="photo-capture-container">
        <div className="photo-capture-card camera-mode">
          <div className="camera-view">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="camera-video"
              style={{ transform: "scaleX(-1)" }}
            ></video>
            <canvas ref={canvasRef} style={{ display: "none" }}></canvas>

            <div className="camera-overlay">
              <div className={`selfie-outline ${faceDetected ? "detected" : ""}`}></div>
              <div className="camera-hint">
                <span>{statusMessage}</span>
              </div>
            </div>

            <div className="camera-controls">
              <button
                className="control-btn cancel"
                onClick={() => setCaptureMode("guide")}
              >
                Cancel
              </button>
              <button
                className="control-btn capture"
                onClick={capturePhoto}
                disabled={!faceDetected}
                title={!faceDetected ? "Position your face to capture" : "Capture"}
              >
                <div className="capture-circle"></div>
              </button>
              <button
                className="control-btn upload"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Review Screen
  if (captureMode === "review") {
    const imageUrl = capturedImage ? URL.createObjectURL(capturedImage) : null;
    return (
      <div className="photo-capture-container">
        <div className="photo-capture-card">
          <div className="photo-capture-header">
            <button className="back-button" onClick={onBack}>
              ←
            </button>
            <div className="header-progress">
              <div
                className="header-progress-fill"
                style={{ width: "85%" }}
              ></div>
            </div>
          </div>

          <h2 className="photo-capture-title">Review your selfie</h2>
          <p className="photo-capture-subtitle">
            Make sure your face is clearly visible and well-lit.
          </p>

          <div className="review-section">
            <div className="image-preview selfie-preview">
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt="Selfie preview"
                  className="preview-image"
                />
              )}
            </div>
          </div>

          <div className="review-buttons">
            <button
              className="review-btn retake"
              onClick={handleRetake}
              disabled={isSubmitting}
            >
              Retake photo
            </button>
            <button
              className="review-btn confirm"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner"></span>
                  Submitting...
                </>
              ) : (
                "Looks good"
              )}
            </button>
          </div>

          <div className="photo-capture-footer">Secured by VerifiLite</div>
        </div>
      </div>
    );
  }

  return null;
}

export default PhotoCapture;
